/** Client helpers for reader AI tools (summary / legal risk). */

import { nevisignFetch } from "@nevisign/reader-core";

export type ReaderTokenUsage = {
  nevisign_tokens: number;
  by_provider: {
    dadno?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
    gemma?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
  };
  profile?: "legal" | "general";
};

export type SummarizeResult = {
  summary: string;
  usage?: ReaderTokenUsage;
  error?: string;
};

export type LegalRiskItem = {
  title: string;
  detail: string;
  severity?: "low" | "medium" | "high";
};

export type LegalRiskCategory = {
  name: string;
  items: LegalRiskItem[];
};

export type LegalRiskResult = {
  overview: string;
  categories: LegalRiskCategory[];
  usage?: ReaderTokenUsage;
  error?: string;
};

export async function summarizeDocument(
  apiBase: string,
  body: {
    document_text: string;
    comments?: string;
    track_changes?: string;
    title?: string;
  },
): Promise<SummarizeResult> {
  if (!apiBase.trim()) throw new Error("آدرس پایه API تنظیم نشده است.");
  const res = await nevisignFetch(apiBase, "/api/reader/summarize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as SummarizeResult;
  if (!res.ok) {
    throw new Error(json.error || json.summary || `HTTP ${res.status}`);
  }
  return json;
}

export async function analyzeLegalRisk(
  apiBase: string,
  body: {
    document_text: string;
    comments?: string;
    track_changes?: string;
    title?: string;
  },
): Promise<LegalRiskResult> {
  if (!apiBase.trim()) throw new Error("آدرس پایه API تنظیم نشده است.");
  const res = await nevisignFetch(apiBase, "/api/reader/legal-risk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as LegalRiskResult;
  if (!res.ok) {
    throw new Error(json.error || json.overview || `HTTP ${res.status}`);
  }
  return json;
}

export type ToolId =
  | "summary"
  | "legal-risk"
  | "assistant"
  | "compare"
  | "word-to-pdf"
  | "pdf-split"
  | "pdf-merge"
  | "to-image"
  | "scan-document";

export type ToolGroup = "ai" | "general";

export type ToolDef = {
  id: ToolId;
  label: string;
  blurb: string;
  envs: Array<"docx" | "pdf" | "home">;
  group: ToolGroup;
  requireAuth?: boolean;
};

export const TOOL_GROUP_ORDER: ToolGroup[] = ["ai", "general"];

export const TOOL_GROUP_LABELS: Record<ToolGroup, string> = {
  ai: "ابزارهای هوش مصنوعی",
  general: "ابزارهای عمومی",
};

export const READER_TOOLS: ToolDef[] = [
  {
    id: "summary",
    label: "خلاصه سند",
    blurb: "خلاصهٔ مدیریتی کل قرارداد",
    envs: ["docx", "pdf", "home"],
    group: "ai",
    requireAuth: true,
  },
  {
    id: "legal-risk",
    label: "تحلیل ریسک حقوقی",
    blurb: "ریسک‌های دسته‌بندی‌شده",
    envs: ["docx", "pdf", "home"],
    group: "ai",
    requireAuth: true,
  },
  {
    id: "assistant",
    label: "دستیار",
    blurb: "گفتگوی هوشمند روی سند",
    envs: ["docx", "pdf", "home"],
    group: "ai",
    requireAuth: true,
  },
  {
    id: "compare",
    label: "مقایسه متن",
    blurb: "مقایسه دو سند در نویساین یا word با نمایش تفاوت‌ها",
    envs: ["docx", "pdf", "home"],
    group: "general",
  },
  {
    id: "word-to-pdf",
    label: "تبدیل Word به PDF",
    blurb: "باز کردن خروجی PDF از مسیر چاپ",
    envs: ["docx", "home"],
    group: "general",
  },
  {
    id: "pdf-split",
    label: "تفکیک صفحات PDF",
    blurb: "برش بین صفحات و دانلود ZIP از بخش‌ها",
    envs: ["pdf", "home"],
    group: "general",
  },
  {
    id: "pdf-merge",
    label: "ادغام اسناد PDF",
    blurb: "ترتیب‌دادن و یکی‌کردن چند PDF",
    envs: ["pdf", "home"],
    group: "general",
  },
  {
    id: "to-image",
    label: "تبدیل PDF به تصویر",
    blurb: "خروجی JPG یا PNG از صفحات PDF",
    envs: ["pdf", "home"],
    group: "general",
  },
  {
    id: "scan-document",
    label: "اسکن سند",
    blurb: "عکس بگیر، برش بزن و PDF بساز",
    envs: ["home"],
    group: "general",
  },
];

export function toolsForEnv(
  env: "docx" | "pdf" | "home"
): ToolDef[] {
  return READER_TOOLS.filter((t) => t.envs.includes(env));
}

export function groupedToolsForEnv(
  env: "docx" | "pdf" | "home"
): Array<{ group: ToolGroup; label: string; tools: ToolDef[] }> {
  const tools = toolsForEnv(env);
  return TOOL_GROUP_ORDER.map((group) => ({
    group,
    label: TOOL_GROUP_LABELS[group],
    tools: tools.filter((t) => t.group === group),
  })).filter((g) => g.tools.length > 0);
}
